import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { Avatar, AvatarFallback } from 'components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { readAuthSession, logout as authLogout } from 'services/authService';

function initialsFromName(name, email) {
  const source = String(name || '').trim() || String(email || '').trim();
  if (!source) return 'U';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

/**
 * Compact profile avatar + menu for the shared AppBar.
 */
export default function AppBarUser() {
  const navigate = useNavigate();
  const session = readAuthSession();
  const displayName = session?.name || 'User';
  const displayEmail = session?.email || '';
  const initials = initialsFromName(displayName, displayEmail);

  const handleLogout = async () => {
    await authLogout();
    navigate('/login', { replace: true });
  };

  if (!session?.email) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="app-bar__profile"
          title={displayName}
          aria-label={`Account menu for ${displayName}`}
        >
          <Avatar className="h-8 w-8">
            <AvatarFallback className="app-bar__profile-fallback text-xs font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-56 rounded-lg" align="end" sideOffset={8}>
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="text-xs font-semibold">{initials}</AvatarFallback>
            </Avatar>
            <div className="grid min-w-0 flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">{displayName}</span>
              {displayEmail ? (
                <span className="truncate text-xs text-muted-foreground">{displayEmail}</span>
              ) : null}
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout}>
          <LogOut />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
